import React, { lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { UserProvider } from './context/UserContext';
import { MessagesProvider, useMessages } from './context/MessagesContext';
import ToastContainer from './components/feedback/ToastContainer';
import CookieBanner from './components/cookies/CookieBanner';
import ProtectedRoute from './components/routes/ProtectedRoute';
import ScrollToTop from './components/routes/ScrollToTop';
import { setMessagesContext } from './services/api';
import { trackPageView } from './services/analytics';

import './App.scss';

const ContentBrowseLayout = lazy(() => import('./layout/parts/ContentBrowseLayout'));
const ProfileLayout = lazy(() => import('./layout/ProfileLayout'));
const Welcome = lazy(() => import('./pages/Welcome'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const Admin = lazy(() => import('./pages/admin/Admin'));
const UserProfile = lazy(() => import('./pages/user/UserProfile'));
const UserProfileEdit = lazy(() => import('./pages/user/UserProfileEdit'));
const ProfileStatsPage = lazy(() => import('./pages/user/ProfileStatsPage'));
const ProfileLeaguesPage = lazy(() => import('./pages/user/ProfileLeaguesPage'));
const ContentFeedPage = lazy(() => import('./pages/content/ContentFeedPage'));
const BookmarksPage = lazy(() => import('./pages/content/BookmarksPage'));
const ContributePage = lazy(() => import('./pages/content/ContributePage'));
const StaticContentPage = lazy(() => import('./pages/static/StaticContentPage'));
const AchievementsPage = lazy(() => import('./pages/user/AchievementsPage'));
const LeaderboardsPage = lazy(() => import('./pages/user/LeaderboardsPage'));
const ChallengeCreatePage = lazy(() => import('./pages/games/ChallengeCreatePage'));
const ChallengePlayPage = lazy(() => import('./pages/games/ChallengePlayPage'));
const ChallengesListPage = lazy(() => import('./pages/games/ChallengesListPage'));
const SurvivalPage = lazy(() => import('./pages/games/SurvivalPage'));
const PublicUserPage = lazy(() => import('./pages/games/PublicUserPage'));
const MainLayout = lazy(() => import('./layout/MainLayout'));
const Container = lazy(() => import('./layout/parts/Container'));

const LegacyContentSlugRedirect: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    return <Navigate to={slug ? `/q/${slug}` : '/'} replace />;
};

const AnalyticsRouteTracker: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname + location.search);
    }, [location.pathname, location.search]);

    return null;
};

const AppWithApiContext: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { addError } = useMessages();

    useEffect(() => {
        setMessagesContext({ addError });
    }, [addError]);

    return <>{children}</>;
};

const App: React.FC = () => {
    return (
        <MessagesProvider>
            <AppWithApiContext>
                <UserProvider>
                    <Router>
                        <ScrollToTop />
                        <AnalyticsRouteTracker />
                        <CookieBanner />
                        <Routes>
                            <Route element={<ContentBrowseLayout />}>
                                <Route path="/" element={<Welcome />} />
                                <Route path="/q/:slug" element={<Welcome />} />
                                <Route path="/content" element={<ContentFeedPage />} />
                                <Route path="/content/:slug" element={<LegacyContentSlugRedirect />} />
                            </Route>

                            {/* Account area — left menu, no browse sidebars */}
                            <Route
                                element={
                                    <ProtectedRoute>
                                        <ProfileLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route path="/profile" element={<UserProfile />} />
                                <Route path="/profile/stats" element={<ProfileStatsPage />} />
                                <Route path="/profile/leagues" element={<ProfileLeaguesPage />} />
                                <Route path="/profile/edit" element={<UserProfileEdit />} />
                                <Route path="/contribute" element={<ContributePage />} />
                                <Route path="/bookmarks" element={<BookmarksPage />} />
                                <Route path="/achievements" element={<AchievementsPage />} />
                                <Route
                                    path="/profile/leaderboards"
                                    element={<Navigate to="/profile/leaderboards/players" replace />}
                                />
                                <Route
                                    path="/profile/leaderboards/:board"
                                    element={<LeaderboardsPage layout="profile" />}
                                />
                            </Route>

                            <Route
                                path="/leaderboards"
                                element={
                                    <ProtectedRoute>
                                        <Navigate to="/leaderboards/players" replace />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/leaderboards/:board"
                                element={
                                    <ProtectedRoute>
                                        <LeaderboardsPage layout="main" />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path="/play/challenge"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout>
                                            <Container>
                                                <ChallengeCreatePage />
                                            </Container>
                                        </MainLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/play/challenge/:id"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout>
                                            <Container>
                                                <ChallengePlayPage />
                                            </Container>
                                        </MainLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/play/challenges"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout>
                                            <Container>
                                                <ChallengesListPage />
                                            </Container>
                                        </MainLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/play/survival"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout>
                                            <Container>
                                                <SurvivalPage />
                                            </Container>
                                        </MainLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/u/:userId"
                                element={
                                    <MainLayout>
                                        <Container>
                                            <PublicUserPage />
                                        </Container>
                                    </MainLayout>
                                }
                            />

                            <Route path="/_storybook" element={<Navigate to="/_adm/storybook" replace />} />
                            <Route path="/oauth/callback" element={<OAuthCallback />} />

                            {/* Static info pages */}
                            <Route path="/about/mission" element={<StaticContentPage pageKey="mission" />} />
                            <Route path="/about/progression" element={<StaticContentPage pageKey="progression" />} />
                            <Route path="/about/creator-progression" element={<StaticContentPage pageKey="creator_progression" />} />
                            <Route path="/about/contact" element={<StaticContentPage pageKey="contact" />} />
                            <Route path="/legal/community-guidelines" element={<StaticContentPage pageKey="community" />} />
                            <Route path="/legal/terms" element={<StaticContentPage pageKey="terms" />} />
                            <Route path="/legal/privacy" element={<StaticContentPage pageKey="privacy" />} />

                            {/* Admin Routes */}
                            <Route path="/_adm/*" element={<Admin />} />
                        </Routes>
                    </Router>
                    <ToastContainer />
                </UserProvider>
            </AppWithApiContext>
        </MessagesProvider>
    );
};

export default App;
